import { Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import {
  TextFieldWithErrorComponent,
  FormSectionComponent,
  ButtonComponent,
  CheckboxFieldComponent
} from 'htx-ui-library';

import { OrganizationMemberApiDto } from '../../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { FormErrorMessagesService } from '../../../../../services/form/form-error-messages.service';
import { NavigationService } from '../../../../../services/navigation.service';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';

/** Roles that can be assigned during the invitation process.
 * ORGANIZATION_REPRESENTATIVE is not directly assignable (transferred via representative workflow).
 * DATASPACE_AUTHORITY is a system-level role and must never be assigned by organisations. */
const ALL_ASSIGNABLE_ROLES = ['ORGANIZATION_USER', 'ORGANIZATION_ADMIN'] as const;

@Component({
  selector: 'app-organization-member-add-page',
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    TextFieldWithErrorComponent,
    FormSectionComponent,
    ButtonComponent,
    CheckboxFieldComponent,
    ProtectedContentComponent
  ],
  templateUrl: './organization-member-add-page.component.html',
  styleUrl: './organization-member-add-page.component.scss'
})
export class OrganizationMemberAddPageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private organizationService = inject(OrganizationService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private navigationService = inject(NavigationService);
  readonly errorMessagesService = inject(FormErrorMessagesService);

  requiredRoles = this.navigationService.getAccessRoles('admin/organizations/members/add');
  fieldLabels: { [key: string]: string } = {};

  /** All roles shown in the UI as checkboxes */
  assignableRoles = ALL_ASSIGNABLE_ROLES;

  organizationId$ = this.activatedRoute.paramMap.pipe(map((p) => p.get('organizationId')!));

  form = this.fb.group({
    firstName: ['', [Validators.required, Validators.maxLength(50)]],
    lastName: ['', [Validators.required, Validators.maxLength(50)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(100)]],
    roles: this.fb.group({
      ORGANIZATION_USER: [{ value: true, disabled: true }],
      ORGANIZATION_ADMIN: [false]
    })
  });

  ngOnInit(): void {
    const controlNames = ['firstName', 'lastName', 'email'];
    controlNames.forEach((name) => {
      this.fieldLabels[name] = this.translocoService.translate(
        `organization.member.add.controls.${name}.label`
      );
    });
  }

  save(): void {
    if (this.form.invalid) {
      return;
    }

    this.activatedRoute.paramMap.subscribe((params) => {
      const organizationId = params.get('organizationId')!;
      const formValue = this.form.getRawValue();

      // ORGANIZATION_USER is always included as a base role
      const selectedRoles: string[] = [
        'ORGANIZATION_USER',
        ...ALL_ASSIGNABLE_ROLES.filter(
          (role) => role !== 'ORGANIZATION_USER' && formValue.roles[role]
        )
      ];

      const memberData: Partial<OrganizationMemberApiDto> = {
        organizationId,
        firstName: formValue.firstName ?? '',
        lastName: formValue.lastName ?? '',
        email: formValue.email ?? '',
        roles: selectedRoles
      };

      this.organizationService.createMember(memberData as OrganizationMemberApiDto).subscribe({
        next: () => {
          this.notificationService.showSuccess('organization.member.add.saveSuccess', 5000);
          void this.router.navigate(['admin', 'organizations', 'details', organizationId]);
        },
        error: () => {
          this.notificationService.showError('organization.member.add.saveError', 5000);
        }
      });
    });
  }

  cancel(): void {
    this.activatedRoute.paramMap.subscribe((params) => {
      const organizationId = params.get('organizationId')!;
      void this.router.navigate(['admin', 'organizations', 'details', organizationId]);
    });
  }
}
